import { useId } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { CircleAlert } from 'lucide-react'
import { cn } from '../primitives/util'

/**
 * Shared label / hint / error chrome for form controls (doctrine §3.4):
 * visible label, hint via aria-describedby, error with role="alert".
 * Controls receive the generated ids through a render callback so the
 * aria wiring is consistent across Input / Textarea / Select.
 */

export interface FieldControlProps {
  id: string
  'aria-describedby': string | undefined
  'aria-invalid': true | undefined
}

export interface FieldProps {
  label?: string
  hint?: string
  error?: string
  /** Renders the control with prebuilt aria props. */
  children: (control: FieldControlProps) => ReactNode
  className?: string
  style?: CSSProperties
}

export function Field({ label, hint, error, children, className, style }: FieldProps) {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined

  return (
    <div className={cn('it-field', className)} style={style}>
      {label && (
        <label className="it-field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {error ? (
        <div className="it-field__error" id={errorId} role="alert">
          <CircleAlert size={13} strokeWidth={2} aria-hidden="true" />
          {error}
        </div>
      ) : (
        hint && (
          <div className="it-field__hint" id={hintId}>
            {hint}
          </div>
        )
      )}
    </div>
  )
}
